import * as THREE from 'three'
import type { GSplatColorInput } from './effectUtils'
import { createColor, writeColorArray } from './effectUtils'
import type { GSplatVisualShowcaseEffectOptions } from './GSplatVisualShowcaseEffect'
import { GSplatVisualShowcaseEffect } from './GSplatVisualShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const electronicShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform float uIntensity;
uniform vec3 uCenter;
uniform float uEffectScale;
uniform float uSwayAmplitude;
uniform float uSwaySpeed;
uniform vec3 uFlickerColor;

${SHOWCASE_SHADER_COMMON_GLSL}

void modifySplatCenter(inout vec3 center) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float sourceHeight = local.z * 2.0 - 1.0;
  local.xz = showcaseRotate2d(
    local.xz,
    smoothstep(-1.0, -2.0, sourceHeight) * uSwayAmplitude * sin(t * uSwaySpeed)
  );
  center = uCenter + local * uEffectScale;
}

vec4 electronicFractal(vec3 local, float t) {
  float m = 100.0;
  vec3 p = local * 0.1;
  p.z += 0.5;

  for (int i = 0; i < 8; i++) {
    p = abs(p) / clamp(abs(p.x * p.z), 0.3, 3.0) - 1.0;
    p.xz = showcaseRotate2d(p.xz, radians(90.0));
    if (i > 1) {
      m = min(
        m,
        length(p.xz) + step(0.3, fract(p.y * 0.5 + t * 0.5 + float(i) * 0.2))
      );
    }
  }

  m = step(m, 0.5) * 1.3 * uIntensity;
  return vec4(uFlickerColor + vec3(-local.z * 0.3, 0.0, 0.0), 0.3) * uIntensity + m;
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  vec4 effectColor = electronicFractal(local, t);
  color = mix(color, color * effectColor, uIntensity);
}
`

const electronicShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn electronicFractal(local: vec3f, t: f32) -> vec4f {
  var m = 100.0;
  var p = local * 0.1;
  p.z = p.z + 0.5;
  for (var i: i32 = 0; i < 8; i++) {
    p = abs(p) / clamp(abs(p.x * p.z), 0.3, 3.0) - 1.0;
    p.xz = showcaseRotate2d(p.xz, 1.5707963);
    if (i > 1) {
      m = min(
        m,
        length(p.xz) + step(0.3, fract(p.y * 0.5 + t * 0.5 + f32(i) * 0.2))
      );
    }
  }
  m = step(m, 0.5) * 1.3 * uniform.uIntensity;
  return vec4f(
    uniform.uFlickerColor + vec3f(-local.z * 0.3, 0.0, 0.0),
    0.3
  ) * uniform.uIntensity + vec4f(m);
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = (*center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let sourceHeight = local.z * 2.0 - 1.0;
  let angle = smoothstep(-1.0, -2.0, sourceHeight) *
    uniform.uSwayAmplitude * sin(t * uniform.uSwaySpeed);
  let rotated = showcaseRotate2d(local.xz, angle);
  (*center) = vec3f(
    uniform.uCenter.x + rotated.x * uniform.uEffectScale,
    (*center).y,
    uniform.uCenter.z + rotated.y * uniform.uEffectScale
  );
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = (center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let effectColor = electronicFractal(local, t);
  *color = mix(*color, *color * effectColor, uniform.uIntensity);
}
`

export interface GSplatVisualElectronicEffectOptions extends GSplatVisualShowcaseEffectOptions {
  swayAmplitude?: number
  swaySpeed?: number
  flickerColor?: GSplatColorInput
}

export class GSplatVisualElectronicEffect extends GSplatVisualShowcaseEffect {
  private readonly _swayAmplitude: number
  private readonly _swaySpeed: number
  private readonly _flickerColor: THREE.Color
  private readonly _flickerColorArray = [0, 0, 0]

  constructor(options: GSplatVisualElectronicEffectOptions = {}) {
    super(
      {
        id: 'visual-electronic',
        shader: electronicShaderGLSL,
        shaderWGSL: electronicShaderWGSL,
      },
      options
    )

    this._swayAmplitude = normalizeNonNegative(options.swayAmplitude, 0.2)
    this._swaySpeed = normalizeNonNegative(options.swaySpeed, 2)
    this._flickerColor = createColor(options.flickerColor, new THREE.Color(0, 0.5, 0.7))
  }

  protected override updateEffect(effectTime: number): void {
    super.updateEffect(effectTime)
    this.setUniform('uSwayAmplitude', this._swayAmplitude)
    this.setUniform('uSwaySpeed', this._swaySpeed)
    this.setUniform('uFlickerColor', writeColorArray(this._flickerColor, this._flickerColorArray))
  }
}

function normalizeNonNegative(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback
}
